import { useState } from "react";
import { ArrowRight, Check, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { api, session } from "../lib/api";
import type { User } from "../types";
import { Button } from "../components/ui";

export default function LoginPage({
  onLogin
}: {
  onLogin: (user: User) => void;
}) {
  const [username, setUsername] = useState(import.meta.env.DEV ? "admin" : "");
  const [password, setPassword] = useState(import.meta.env.DEV ? "Admin123!" : "");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await api.login(username, password);
      session.token = result.token;
      onLogin(result.user);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page">
      <div className="login-brand-panel">
        <div className="login-brand">
          <span className="brand-mark">
            <Mail size={23} strokeWidth={2.4} />
          </span>
          <span>Unify Mail</span>
        </div>
        <div className="login-story">
          <span className="eyebrow light">Team email, finally organized</span>
          <h1>One calm place for every shared conversation.</h1>
          <p>
            Give your team the access they need while provider credentials stay encrypted and
            completely out of sight.
          </p>
          <ul>
            <li>
              <Check size={17} /> Shared mailboxes with precise permissions
            </li>
            <li>
              <Check size={17} /> Consistent, editable email templates
            </li>
            <li>
              <Check size={17} /> Complete viewing and sending history
            </li>
          </ul>
        </div>
        <div className="security-note">
          <ShieldCheck size={20} />
          <div>
            <strong>Credentials stay server-side</strong>
            <span>Passwords and provider tokens never reach this desktop client.</span>
          </div>
        </div>
        <div className="orb orb-one" />
        <div className="orb orb-two" />
      </div>

      <div className="login-form-panel">
        <form className="login-card" onSubmit={submit}>
          <span className="mobile-brand">
            <span className="brand-mark">
              <Mail size={21} />
            </span>
            Unify Mail
          </span>
          <span className="eyebrow">Secure workspace</span>
          <h2>Welcome back</h2>
          <p>Sign in to manage your team’s email.</p>

          <label className="field">
            <span>Username</span>
            <div className="input-with-icon">
              <Mail size={17} />
              <input
                autoFocus
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Your username"
              />
            </div>
          </label>
          <label className="field">
            <span>Password</span>
            <div className="input-with-icon">
              <LockKeyhole size={17} />
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Your password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </label>
          {error && <div className="form-error">{error}</div>}
          <Button className="login-button" type="submit" disabled={loading}>
            {loading ? "Signing in…" : "Sign in"}
            {!loading && <ArrowRight size={17} />}
          </Button>
          {import.meta.env.DEV && (
            <div className="demo-credentials">
              <strong>Local development workspace</strong>
              <span>
                Username <code>admin</code> &middot; Password <code>Admin123!</code>
              </span>
            </div>
          )}
        </form>
        <p className="login-footnote">Protected by encrypted server storage and server-side access control.</p>
      </div>
    </main>
  );
}
